import { ArrowUpRight, Mail, MessageCircle, PackageSearch, LifeBuoy, MessagesSquare } from "lucide-react";
import { FadeUp, MaskedLines } from "@/components/Reveal";

const WHATSAPP = "https://wa.me/919555558183";
const EMAIL = "helpdesk@getvibe.co.in";

const TOPICS = [
  { icon: PackageSearch, label: "Order help", text: "Track, update or ask about your order." },
  { icon: LifeBuoy, label: "Have an issue", text: "Something wrong with your delivery or pack? We'll fix it." },
  { icon: MessagesSquare, label: "Anything else", text: "Questions about Vibe, ingredients or your routine." },
];

export default function Contact() {
  return (
    <div data-testid="page-contact">
      <section className="mx-auto max-w-[88rem] px-5 sm:px-10 pt-[7.5rem] lg:pt-[8.5rem] pb-8 lg:pb-10">
        <FadeUp>
          <div className="rounded-2xl bg-[#E4EFE6] px-8 py-8 sm:px-14 sm:py-11">
            <p className="text-xs uppercase tracking-[0.26em] text-[#4A6B56] mb-4">Contact Vibe</p>
            <h1 className="font-display text-[clamp(2.4rem,5vw,4.4rem)] leading-[1.05] text-[#1B4332] max-w-4xl">
              <MaskedLines onView lines={["Have an issue? We're here."]} />
            </h1>
            <FadeUp delay={0.2}>
              <p className="mt-5 max-w-2xl text-lg text-[#4A6B56] leading-relaxed">
                Order help, an issue with your pack, or anything else — reach us directly on
                WhatsApp or email and we'll sort it out with you.
              </p>
            </FadeUp>
          </div>
        </FadeUp>
      </section>

      <section className="mx-auto max-w-[88rem] px-5 sm:px-10 pb-16 lg:pb-20">
        <div className="grid md:grid-cols-2 gap-5">
          <FadeUp className="h-full">
            <a
              href={WHATSAPP}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-whatsapp-card"
              className="group flex flex-col rounded-xl bg-[#1B4332] p-8 sm:p-10 h-full cursor-pointer transition-all duration-500 hover:-translate-y-1"
            >
              <span className="grid place-items-center h-14 w-14 rounded-full bg-[#E8F3EB] text-[#1B4332]">
                <MessageCircle size={22} strokeWidth={1.6} />
              </span>
              <p className="mt-7 text-[0.68rem] font-bold uppercase tracking-[0.18em] text-[#A7C8B2]">
                WhatsApp — fastest reply
              </p>
              <p className="mt-3 font-display text-3xl sm:text-4xl text-[#E8F3EB]" data-testid="contact-whatsapp-number">
                +91 95555 58183
              </p>
              <p className="mt-3 text-[#DCEADF]">Message us anytime — we usually reply within hours.</p>
              <span className="mt-auto pt-8 inline-flex items-center gap-1.5 text-[#E8F3EB] font-semibold">
                Chat on WhatsApp
                <ArrowUpRight
                  size={16}
                  className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                />
              </span>
            </a>
          </FadeUp>

          <FadeUp delay={0.1} className="h-full">
            <a
              href={`mailto:${EMAIL}`}
              data-testid="contact-email-card"
              className="animate-card-highlight group flex flex-col rounded-xl bg-white p-8 sm:p-10 h-full cursor-pointer ring-2 ring-[#A7C8B2] transition-all duration-500 hover:-translate-y-1"
            >
              <span className="grid place-items-center h-14 w-14 rounded-full border border-[#C6DCCB] text-[#1B4332]">
                <Mail size={22} strokeWidth={1.6} />
              </span>
              <p className="mt-7 text-[0.68rem] font-bold uppercase tracking-[0.18em] text-[#4A6B56]">
                Email — helpdesk
              </p>
              <p className="mt-3 font-display text-3xl sm:text-4xl text-[#1B4332]" data-testid="contact-email-address">
                helpdesk@getvibe.co.in
              </p>
              <p className="mt-3 text-[#4A6B56]">Write to us about your order, an issue, or anything else.</p>
              <span className="mt-auto pt-8 inline-flex items-center gap-1.5 text-[#1B4332] font-semibold">
                Write an email
                <ArrowUpRight
                  size={16}
                  className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                />
              </span>
            </a>
          </FadeUp>
        </div>

        <div className="grid md:grid-cols-3 gap-5 mt-5">
          {TOPICS.map((topic, i) => (
            <FadeUp key={topic.label} delay={i * 0.08} className="h-full">
              <div
                data-testid={`contact-topic-${i}`}
                className="rounded-xl bg-[#F1F8F2] border border-[#D1E2D6]/60 p-7 h-full"
              >
                <span className="grid place-items-center h-11 w-11 rounded-full border border-[#C6DCCB] text-[#1B4332]">
                  <topic.icon size={17} strokeWidth={1.6} />
                </span>
                <p className="mt-5 font-semibold text-[#1B4332]">{topic.label}</p>
                <p className="mt-2 text-sm text-[#4A6B56] leading-relaxed">{topic.text}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>
    </div>
  );
}
